import React, { useCallback, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import ModalContainer from './ModalContainer';
import {
  multiModalCustomContenCleared,
  needToKnowModalVisibleChanged,
  petCoPayModalVisibleChanged,
  termsAndCondModalVisibleChanged,
  wellnessVisibleChanged,
} from '../../actions/quoting';
import { MODAL_RELATION_LIST } from '../../constants';
import { useResponsive, useOnEscEvent } from '../../hooks';
import { useElectronicConsent } from '../../util';
import { Accordion } from './Accordion';
import { HtmlContent } from './HtmlContent';
import ScrollableDiv from './ScrollableDiv';
import './MultiModalAsCollapsible.css';

const ELECTRONIC_CONSENT_MODAL_ID = 10;

const MultiModalAccordionItem = ({ content }) => (
  <div className="Multimodal-as-collapsible-accordion-item">
    {content.map((item) => (
      <>
        <h3>
          {item?.BulletIconUrl
            ? (
              <img
                alt=""
                className="Multimodal-as-collapsible-accordion-item-img"
                src={item.BulletIconUrl}
              />
            )
            : null}

          <HtmlContent
            classNameContainer="Multimodal-as-collapsible-accordion-item-span"
            content={item.ItemLabel}
            elementId={`title-${item.Id}`}
            elementType="span"
          />
        </h3>

        <HtmlContent
          classNameContainer="Multimodal-as-collapsible-accordion-item-p"
          content={item.ItemSubText}
          elementId={`subtitle-${item.Id}`}
          elementType="p"
        />
      </>
    ))}
  </div>
);

const MultiModalAsCollapsible = ({
  modalId = 0,
  show = false,
  keepInDOM = false,
  id = '',
}) => {
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const dispatch = useDispatch();
  const {
    multiModalCustomData,
    multiModalData,
    multiModalUserSelection: { needToKnowIdSelected },
    rateSummary: { PetQuotes },
  } = store;
  const {
    NEED_TO_KNOW_MODAL_ID,
    PET_CO_PAY_MODAL_ID,
    TERMS_AND_COND_MODAL_ID,
    WELLNESS_MODAL_ID,
  } = MODAL_RELATION_LIST;
  const { isTablet, isMobile } = useResponsive();

  function isElectronicConsent() {
    if (PetQuotes && PetQuotes[0]) {
      return PetQuotes[0].IsElectronicConsent;
    }

    return false;
  }

  const modalTitle = useMemo(() => {
    if (multiModalData.InsuranceMultiModalSectionDtoList.length) {
      const data = multiModalData.InsuranceMultiModalSectionDtoList
        .find((element) => element.Id === modalId);

      return data ? data.SectionName : '';
    }

    return '';
  }, [modalId, multiModalData]);

  const categoryListMemo = useMemo(() => {
    if (multiModalCustomData) {
      return multiModalCustomData.category;
    }

    if (multiModalData.InsuranceMultiModalSectionDtoList.length) {
      const categoryList = multiModalData
        .InsuranceMultiModalSectionDtoList
        .find((element) => element.Id === modalId) || {};

      if (useElectronicConsent) {
        if (modalId === TERMS_AND_COND_MODAL_ID
          && !isElectronicConsent()) {
          return categoryList.InsuranceMultiModalCategoryDtoList
            .filter((element) => element.Id !== ELECTRONIC_CONSENT_MODAL_ID);
        }
      }

      const items = categoryList.InsuranceMultiModalCategoryDtoList || [];
      if (isMobile) {
        return items.filter((category) => category.IsMobile);
      }

      return items;
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalId, multiModalCustomData, multiModalData, isMobile]);

  const onCloseModal = useCallback(() => {
    if (multiModalCustomData) {
      dispatch(multiModalCustomContenCleared());
      return;
    }

    const visible = false;

    switch (modalId) {
      case NEED_TO_KNOW_MODAL_ID: {
        dispatch(needToKnowModalVisibleChanged({
          categoryId: needToKnowIdSelected,
          visible,
        }));
        break;
      }

      case PET_CO_PAY_MODAL_ID:
        dispatch(petCoPayModalVisibleChanged({ visible }));
        break;

      case TERMS_AND_COND_MODAL_ID:
        dispatch(termsAndCondModalVisibleChanged({ visible }));
        break;

      case WELLNESS_MODAL_ID:
        dispatch(wellnessVisibleChanged({ visible }));
        break;

      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, modalId, multiModalCustomData, needToKnowIdSelected, show]);

  useOnEscEvent(() => {
    if (show) {
      onCloseModal();
    }
  }, [onCloseModal, show]);

  const categoryListMapped = useMemo(() => categoryListMemo.map((category) => ({
    content: category.InsuranceMultiModalItemsDtoList,
    id: category.Id,
    title: category.CategoryHeader,
  })), [categoryListMemo]);

  return (
    <ModalContainer
      ariaTitle={modalTitle}
      cancelable
      classCloseIcon="Modal-card-button-close-icon"
      disableClickOutside={isTablet}
      handleOutClick={onCloseModal}
      id={id}
      idPrefix={id}
      isMultiModal
      keepInDOM={keepInDOM}
      show={show}
    >
      <ScrollableDiv className="Multimodal-as-collapsible" scrollbarMargin>
        <h1 className="Multimodal-as-collapsible-title">{modalTitle}</h1>

        <Accordion
          ContentComponent={MultiModalAccordionItem}
          items={categoryListMapped}
          uniqueExpaned
        />
      </ScrollableDiv>
    </ModalContainer>
  );
};

export default MultiModalAsCollapsible;
